import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Heart, Sparkles } from 'lucide-react';
import { FadeUp, Stagger, StaggerItem } from '../components/Motion';
import api from '../lib/api';
import { resolveImg } from '../lib/img';

export default function Sponsorship() {
  const { t, i18n } = useTranslation();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ sponsor_name: '', sponsor_email: '', message: '' });
  const [error, setError] = useState('');
  const lng = i18n.language?.startsWith('en') ? 'en' : 'fr';

  useEffect(() => {
    api.get('/sponsorships').then((r) => setPlans(r.data)).catch(() => {});
  }, []);

  const handleCheckout = async (plan) => {
    setLoading(true);
    setError('');
    try {
      const origin = window.location.origin;
      const { data } = await api.post('/sponsorships/checkout-session', {
        plan_key: plan.key,
        sponsor_name: form.sponsor_name,
        sponsor_email: form.sponsor_email || undefined,
        message: form.message,
        origin,
      });
      if (data.url) {
        window.location.href = data.url;
      } else {
        setError(t('sponsorship.error_generic'));
      }
    } catch (e) {
      setError(e?.response?.data?.detail || t('sponsorship.error_generic'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="sponsorship-page" className="pt-32 pb-24">
      <section className="container-x max-w-7xl mx-auto pb-16">
        <FadeUp>
          <p className="overline">{t('sponsorship.overline')}</p>
          <h1 className="mt-4 font-heading text-5xl sm:text-6xl lg:text-7xl text-ink tracking-tight max-w-4xl text-balance">
            {t('sponsorship.title')}
          </h1>
          <p className="mt-6 text-lg text-ink-soft max-w-2xl leading-relaxed">{t('sponsorship.intro')}</p>
        </FadeUp>
      </section>

      <section className="container-x max-w-7xl mx-auto">
        <Stagger className="grid gap-8 lg:grid-cols-3">
          {plans.map((p) => (
            <StaggerItem key={p.id}>
              <article
                data-testid={`plan-${p.key}`}
                className={`relative rounded-3xl overflow-hidden border ${
                  p.highlighted ? 'border-forest shadow-xl lg:-translate-y-4' : 'border-border-soft'
                } bg-white flex flex-col h-full`}
              >
                {p.highlighted && (
                  <div className="absolute top-4 right-4 z-10 bg-sun text-ink text-xs font-bold uppercase tracking-widest rounded-full px-3 py-1 flex items-center gap-1">
                    <Sparkles size={12} /> {t('sponsorship.popular')}
                  </div>
                )}
                {p.image && (
                  <div className="aspect-[16/10] overflow-hidden">
                    <img src={resolveImg(p.image)} alt={p[`name_${lng}`]} className="w-full h-full object-cover" />
                  </div>
                )}
                <div className="p-8 flex flex-col flex-1">
                  <h3 className="font-heading text-3xl text-ink tracking-tight">{p[`name_${lng}`]}</h3>
                  <p className="mt-4 text-ink-soft leading-relaxed text-[15px]">{p[`description_${lng}`]}</p>
                  <div className="mt-6 flex items-baseline gap-2">
                    <span className="font-heading text-5xl text-forest">{p.price_eur.toFixed(0)}€</span>
                    <span className="text-sm text-ink-muted">{p[`duration_label_${lng}`]}</span>
                  </div>
                  <ul className="mt-6 grid gap-3 flex-1">
                    {(p[`perks_${lng}`] || []).map((perk) => (
                      <li key={perk} className="flex items-start gap-2 text-ink-soft text-sm">
                        <Check size={16} className="text-forest mt-0.5 shrink-0" />
                        <span>{perk}</span>
                      </li>
                    ))}
                  </ul>
                  <button
                    type="button"
                    onClick={() => setSelected(p)}
                    data-testid={`select-plan-${p.key}`}
                    disabled={loading}
                    className={`mt-8 w-full inline-flex items-center justify-center gap-2 ${p.highlighted ? 'btn-primary' : 'btn-outline'} py-3`}
                  >
                    <Heart size={16} /> {t('sponsorship.choose')}
                  </button>
                </div>
              </article>
            </StaggerItem>
          ))}
        </Stagger>
      </section>

      {/* MODAL */}
      {selected && (
        <div className="fixed inset-0 z-50 bg-ink/80 backdrop-blur-sm grid place-items-center p-4" onClick={() => !loading && setSelected(null)}>
          <div
            className="bg-white rounded-3xl max-w-lg w-full p-8 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="sponsorship-modal"
          >
            <p className="overline mb-2">{t('sponsorship.your_contribution')}</p>
            <h3 className="font-heading text-3xl text-ink tracking-tight">{selected[`name_${lng}`]}</h3>
            <p className="mt-2 font-heading text-4xl text-forest">{selected.price_eur.toFixed(0)} €</p>

            <div className="mt-6 grid gap-4">
              <input
                type="text"
                placeholder={t('sponsorship.sponsor_name')}
                value={form.sponsor_name}
                onChange={(e) => setForm({ ...form, sponsor_name: e.target.value })}
                className="w-full px-4 py-3 border border-border-soft rounded-xl bg-bone focus:outline-none focus:border-forest"
                data-testid="sponsor-name-input"
              />
              <input
                type="email"
                placeholder={t('sponsorship.sponsor_email')}
                value={form.sponsor_email}
                onChange={(e) => setForm({ ...form, sponsor_email: e.target.value })}
                className="w-full px-4 py-3 border border-border-soft rounded-xl bg-bone focus:outline-none focus:border-forest"
                data-testid="sponsor-email-input"
              />
              <textarea
                placeholder={t('sponsorship.sponsor_message')}
                rows={3}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="w-full px-4 py-3 border border-border-soft rounded-xl bg-bone focus:outline-none focus:border-forest resize-none"
                data-testid="sponsor-message-input"
              />
            </div>

            {error && (
              <p className="mt-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-2" data-testid="sponsorship-error">{error}</p>
            )}

            <div className="mt-6 flex gap-3">
              <button
                type="button"
                onClick={() => setSelected(null)}
                disabled={loading}
                className="btn-outline flex-1"
                data-testid="sponsorship-cancel"
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={() => handleCheckout(selected)}
                disabled={loading}
                className="btn-primary flex-1"
                data-testid="sponsorship-confirm"
              >
                {loading ? t('sponsorship.loading') : t('sponsorship.proceed_payment')}
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
